/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { Nav, Tab } from "react-bootstrap";
import {
	BUILT_IN_TABS,
	CONTACT_TYPES,
	CUSTOMER_PLATFORMS,
	TAB_TYPES,
	TAB_VARIANTS,
} from "../../../../utility/constants";
import {
	GeneralTabContent,
	AttributesTabContent,
	CallHistoryTabContent,
	IFrameTabContent,
	NotesTabContent,
	ExternalTransfersTabContent,
	CannedResponsesTabContent,
	RealtimeSentimentAnalysisContent,
	OutboundSMSNotificationTabContent,
	EmailTabContent,
	VoicemailTabContent,
	VideoMeetingTabContent,
} from "./TabContents";
import "./TabLayout.css";
import Connect from "../../../../utility/connect-endpoints";
import { tabShouldShow } from "../../utility";

const getPlatform = (attributes) => {
	let general_platform = "";
	if (attributes) {
		if (Array.isArray(attributes)) {
			general_platform = attributes.find((attr) => attr.name === "general_platform");
		} else {
			general_platform = attributes.general.find((attr) => attr.id === "general_platform");
		}
		return general_platform?.value;
	} else {
		return "";
	}
};

// choose a component to render based on tab attributes and configuration
const renderTab = (tabConfig, contact, caller, contactNotes, setNotesForContact, renderToastContent, activeTab) => {
	if (tabConfig.id === BUILT_IN_TABS.general.id) {
		return <GeneralTabContent attributes={tabConfig.attributes} contact={contact} caller={caller} />;
	}

	if (tabConfig.type === TAB_TYPES.attribute && tabConfig.config?.attributePrefix) {
		return <AttributesTabContent attributes={tabConfig.attributes} contact={contact} />;
	}

	if (tabConfig.id === BUILT_IN_TABS.notes.id) {
		return (
			<NotesTabContent
				contact={contact}
				contactNotes={contactNotes}
				setNotesForContact={setNotesForContact}
				renderToastContent={renderToastContent}
			/>
		);
	}

	if (
		tabConfig.id === BUILT_IN_TABS.voicemail_task_tab.id &&
		getPlatform(contact?.attributes) === CUSTOMER_PLATFORMS.VOICEMAIL
	) {
		return <VoicemailTabContent contact={contact} renderToastContent={renderToastContent} activeTab={activeTab} />;
	}

	if (tabConfig.id === BUILT_IN_TABS.connect_realtime_sentiment_analysis.id) {
		return <RealtimeSentimentAnalysisContent contact={contact} activeTab={activeTab} />;
	}
	if (tabConfig.id === BUILT_IN_TABS.canned_responses.id) {
		return <CannedResponsesTabContent contact={contact} />;
	}

	if (tabConfig.id === BUILT_IN_TABS.contact_history.id) {
		return <CallHistoryTabContent contact={contact} activeTab={activeTab} />;
	}

	if (tabConfig.id === BUILT_IN_TABS.external_transfers.id) {
		return <ExternalTransfersTabContent contact={contact} renderToastContent={renderToastContent} />;
	}

	if (tabConfig.id === BUILT_IN_TABS.pinpoint_outbound_sms.id) {
		return <OutboundSMSNotificationTabContent contact={contact} renderToastContent={renderToastContent} />;
	}

	if (tabConfig.id === BUILT_IN_TABS.video_meeting_tab.id) {
		return <VideoMeetingTabContent attributes={contact?.attributes} />;
	}

	if (tabConfig.id === BUILT_IN_TABS.email.id) {
		return <EmailTabContent contact={contact} renderToastContent={renderToastContent} />;
	}

	if (tabConfig.type === TAB_TYPES.amazon) {
		return (
			<div style={{ flex: 1, display: "flex" }} className={"amazon-iframe"} id={`${tabConfig.id}_container`}>
				<div style={{ color: "grey", margin: "auto", textAlign: "center" }}>
					<div>Please contact the PACE MSO team to enable this feature.</div>
				</div>
			</div>
		);
	}
	if (tabConfig.type === TAB_TYPES.embed) {
		return <IFrameTabContent contact={contact} tabConfig={tabConfig} />;
	}

	return null;
};

// Main tab layout component responsible for creating tab content
const TabLayout = ({
	tabConfig,
	contact,
	caller,
	attributes,
	setNotesForContact,
	contactNotes,
	renderToastContent,
}) => {
	const [key, setKey] = React.useState("");
	const [tabs, setTabs] = React.useState([]);
	const [initialized, setInitialized] = React.useState({});
	React.useEffect(() => {
		if (!tabs || !tabs.length) {
			return;
		}
		const visibleTabs = tabs && tabs.filter((t) => contact || t.variant === TAB_VARIANTS.static);
		if (!visibleTabs.find((t) => t.id === key)) {
			const newKey = visibleTabs && visibleTabs.length ? visibleTabs[0].id : "";
			if (newKey !== key) {
				setKey(newKey);
			}
		}
	}, [tabs, key, contact]);

	React.useEffect(() => {
		const amazonTabs = tabs.filter((t) => t.type === TAB_TYPES.amazon && !initialized[t.id]);
		if (amazonTabs.length > 0) {
			const newInit = { ...initialized };
			let change = false;
			amazonTabs.forEach((t) => {
				const container = document.getElementById(`${t.id}_container`);
				if (container && !initialized[t.id]) {
					newInit[t.id] = true;
					change = true;
					if (tabConfig[`${t.config.amazonConnectFeature}Enabled`]) {
						container.removeChild(container.firstElementChild);
						window.connect.agentApp.initApp(
							t.config.amazonConnectFeature,
							`${t.id}_container`,
							Connect[t.config.amazonConnectFeature],
						);
					}
				}
			});
			if (change) setInitialized(newInit);
		}
	}, [tabConfig, tabs, initialized]);

	React.useEffect(() => {
		const tabs = tabConfig.tabs
			.filter((t) => tabShouldShow(contact, t))
			.map((t) => {
				let attrs = null;

				if (attributes && t.type === TAB_TYPES.attribute && t.config?.attributePrefix) {
					attrs = attributes[t.config?.attributePrefix];
				}
				const isAttributeTabWithoutAttributes =
					t.type === TAB_TYPES.attribute && t.id !== BUILT_IN_TABS.general.id && (!attrs || !attrs.length);

				const shouldHideExternalTransfers =
					t.variant !== TAB_VARIANTS.static &&
					t.id === BUILT_IN_TABS.external_transfers.id &&
					[window.connect.ContactType.VOICE, window.connect.ContactType.CHAT].includes(contact?.type) === false;

				const shouldHideSentimentAnalysis =
					t.variant !== TAB_VARIANTS.static &&
					t.id === BUILT_IN_TABS.connect_realtime_sentiment_analysis.id &&
					contact?.type !== window.connect.ContactType.VOICE;

				const shouldHideCannedResponses =
					t.variant !== TAB_VARIANTS.static &&
					t.id === BUILT_IN_TABS.canned_responses.id &&
					(contact?.type === window.connect.ContactType.VOICE ||
						[CUSTOMER_PLATFORMS.EMAIL, CUSTOMER_PLATFORMS.VOICEMAIL].includes(getPlatform(attributes)));

				const shouldHideEmail = t.id === BUILT_IN_TABS.email.id && getPlatform(attributes) !== CUSTOMER_PLATFORMS.EMAIL;

				const shouldHideVoicemailTab =
					t.variant !== TAB_VARIANTS.static &&
					t.id === BUILT_IN_TABS.voicemail_task_tab.id &&
					getPlatform(attributes) !== CUSTOMER_PLATFORMS.VOICEMAIL;

				const shouldHideMeetingTab =
					t.variant !== TAB_VARIANTS.static &&
					t.id === BUILT_IN_TABS.video_meeting_tab.id &&
					getPlatform(attributes) !== CUSTOMER_PLATFORMS.WEB_CHAT &&
					contact?.type?.toUpperCase() !== CONTACT_TYPES.CHAT;

				return {
					...t,
					displayName: t.type === TAB_TYPES.builtIn ? BUILT_IN_TABS[t.id].displayName : t.displayName,
					attributes: attrs,
					// we want to hide tabs without any attributes
					hidden:
						isAttributeTabWithoutAttributes ||
						shouldHideExternalTransfers ||
						shouldHideCannedResponses ||
						shouldHideMeetingTab ||
						shouldHideSentimentAnalysis ||
						shouldHideEmail ||
						shouldHideVoicemailTab,
				};
			})
			.filter((t) => !t.hidden);

		setTabs(tabs);
	}, [tabConfig, attributes, contact]);
	return (
		<>
			{!tabs.length ? (
				<div className="no-tabs-msg">
					<div className="d-inline w-100">
						<h4>No tabs found</h4>
						<span>There are currently no tabs to display or they have been disabled by your administrator.</span>
					</div>
				</div>
			) : (
				<Tab.Container defaultActiveKey={key} activeKey={key} onSelect={(k) => setKey(k)}>
					<Nav className="custom-scrollbar tab-navbar-container" style={{ paddingRight: 1 }} variant="tabs">
						{tabs.map((t) => (
							<Nav.Item key={t.id} title={t.description}>
								<Nav.Link
									className="tab-item-link"
									style={{
										backgroundColor: t.id === key ? "#e8e8e8" : "whitesmoke",
										fontWeight: t.id === key ? "1000" : "500",
										fontSize: "0.875rem",
										fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
										color: "#666",
									}}
									eventKey={t.id}
								>
									{t.displayName}
								</Nav.Link>
							</Nav.Item>
						))}
					</Nav>
					<Tab.Content className="tab-content-container">
						{tabs.map((t) => (
							<Tab.Pane key={t.id} eventKey={t.id} hidden={key !== t.id}>
								{renderTab(t, contact, caller, contactNotes, setNotesForContact, renderToastContent, key)}
							</Tab.Pane>
						))}
					</Tab.Content>
				</Tab.Container>
			)}
		</>
	);
};

export default TabLayout;
